import { Transaction } from "@solana/web3.js";
import { Base58Util } from "@solomon-labs/base58";
import { BigDecimal } from "@solomon-labs/bigdecimal";

import { ClaimApiError } from "./ClaimApiClient";
import type { ClaimApi, ClaimAttempt, ClaimRequest, ClaimWallet } from "./claimTypes";

export interface ClaimJournal {
    requestId: string;
    requestUuid?: string;
    signatures: Record<string, string>;
}
export interface ClaimStorage {
    read(): ClaimJournal | null;
    write(value: ClaimJournal): void;
}
export interface ClaimChain {
    estimate(attempt: ClaimAttempt): Promise<string>;
    isValid(attempt: ClaimAttempt): Promise<boolean>;
    submit(attempt: ClaimAttempt, bytes: Uint8Array): Promise<string>;
}
export interface ClaimReview {
    request: ClaimRequest;
    fees: Record<string, string>;
}
export function browserClaimStorage(scopeKey: string): ClaimStorage {
    const key = `yaas:claim:v1:${scopeKey}`;
    return {
        read(): ClaimJournal | null {
            const text = localStorage.getItem(key);
            if (!text) return null;
            const value: unknown = JSON.parse(text);
            if (
                !value ||
                typeof value !== "object" ||
                !("requestId" in value) ||
                typeof value.requestId !== "string" ||
                !("signatures" in value) ||
                !value.signatures ||
                typeof value.signatures !== "object"
            )
                throw new Error("Saved claim state could not be read");
            return value as ClaimJournal;
        },
        write: (value) => localStorage.setItem(key, JSON.stringify(value))
    };
}
export class ClaimFlow {
    private busy = false;
    constructor(
        readonly api: ClaimApi,
        private readonly storage: ClaimStorage,
        private readonly chain: ClaimChain
    ) {}
    async prepare(wallet: ClaimWallet, onRequest: (request: ClaimRequest) => void = () => {}): Promise<ClaimReview> {
        return this.exclusive(async () => {
            await this.api.authorize(wallet);
            let saved = this.storage.read();
            if (saved?.requestUuid) {
                // Deliver locally saved signatures before replacing a review, including a lost submit response.
                let previous = await this.api.status(saved.requestUuid);
                for (const [attemptUuid, signature] of Object.entries(saved.signatures)) {
                    const attempt = previous.attempts.find((a) => a.attemptUuid === attemptUuid);
                    if (attempt && attempt.state !== "paid" && attempt.state !== "expired_unexecuted")
                        previous = await this.api.report(saved.requestUuid, attemptUuid, signature);
                }
                const pending = previous.attempts.filter(
                    (a) => !["paid", "expired_unexecuted", "superseded"].includes(a.state)
                );
                // A fresh Jachin balance and cumulative claim allow replacement after blockhash expiry.
                // RPC failures preserve the journal; this never signs or rebroadcasts automatically.
                const validity = await Promise.all(pending.map((a) => this.chain.isValid(a)));
                if (pending.length && validity.every(Boolean)) {
                    onRequest(previous);
                    return this.review(previous);
                }
                saved = null;
            }
            if (!saved) {
                saved = { requestId: crypto.randomUUID(), signatures: {} };
                this.storage.write(saved);
            }
            let request: ClaimRequest;
            try {
                request = await this.api.prepare(saved.requestId);
            } catch (error) {
                if (
                    !(error instanceof ClaimApiError) ||
                    error.status !== 409 ||
                    error.code !== "preparation_abandoned" ||
                    saved.requestUuid ||
                    Object.keys(saved.signatures).length
                )
                    throw error;
                // The server proves this preparation exposed no transaction; a fresh replay key is safe.
                saved = { requestId: crypto.randomUUID(), signatures: {} };
                this.storage.write(saved);
                request = await this.api.prepare(saved.requestId);
            }
            this.storage.write({ ...saved, requestUuid: request.claimRequestUuid });
            onRequest(request);
            return this.review(request);
        });
    }
    async review(request: ClaimRequest): Promise<ClaimReview> {
        const fees: Record<string, string> = {};
        for (const attempt of request.attempts.filter((a) => a.requiredAction === "sign_and_submit"))
            fees[attempt.attemptUuid] = await this.chain.estimate(attempt);
        return { request, fees };
    }
    async recover(wallet: ClaimWallet): Promise<ClaimRequest | null> {
        await this.api.authorize(wallet);
        let saved = this.storage.read();
        if (!saved?.requestUuid) {
            const active = (await this.api.rewards())?.activeClaims?.[0];
            if (!active) return null;
            saved = {
                requestId: saved?.requestId ?? crypto.randomUUID(),
                requestUuid: active.claimRequestUuid,
                signatures: {}
            };
            this.storage.write(saved);
        }
        const current = await this.api.status(saved.requestUuid!);
        for (const [attempt, signature] of Object.entries(saved.signatures)) {
            const state = current.attempts.find((a) => a.attemptUuid === attempt)?.state;
            if (state && state !== "paid" && state !== "expired_unexecuted")
                await this.api.report(saved.requestUuid!, attempt, signature);
        }
        return this.api.status(saved.requestUuid!);
    }
    async submit(
        review: ClaimReview,
        wallet: ClaimWallet,
        onProgress: (request: ClaimRequest) => void
    ): Promise<ClaimRequest> {
        return this.exclusive(async () => {
            await this.api.authorize(wallet);
            const saved = this.storage.read();
            if (saved?.requestUuid !== review.request.claimRequestUuid)
                throw new Error("Claim review no longer matches the saved request");
            let current = await this.api.status(saved.requestUuid);
            for (const attempt of current.attempts) {
                if (["paid", "expired_unexecuted", "superseded"].includes(attempt.state)) continue;
                if (saved.signatures[attempt.attemptUuid]) {
                    current = await this.api.report(
                        saved.requestUuid,
                        attempt.attemptUuid,
                        saved.signatures[attempt.attemptUuid]
                    );
                    onProgress(current);
                    continue;
                }
                if (attempt.requiredAction !== "sign_and_submit") continue;
                const reviewed = review.request.attempts.find((a) => a.attemptUuid === attempt.attemptUuid);
                if (
                    !reviewed ||
                    reviewed.transaction !== attempt.transaction ||
                    attempt.feePayerAddress !== wallet.address ||
                    !review.fees[attempt.attemptUuid]
                )
                    throw new Error("Review this transaction with its fee-paying wallet");
                if (!(await this.chain.isValid(attempt)))
                    throw new Error("Transaction expired. Prepare the claim again.");
                if (
                    new BigDecimal(await this.chain.estimate(attempt)).gt(
                        new BigDecimal(review.fees[attempt.attemptUuid])
                    )
                )
                    throw new Error("The estimated fee increased. Review the claim again.");
                const original = Transaction.from(
                    Base58Util.decode(attempt.transaction as Parameters<typeof Base58Util.decode>[0])
                );
                const bytes = await wallet.signTransaction(
                    Base58Util.decode(attempt.transaction as Parameters<typeof Base58Util.decode>[0])
                );
                const signed = Transaction.from(bytes);
                if (
                    !signed.serializeMessage().equals(original.serializeMessage()) ||
                    !signed.signature ||
                    !signed.verifySignatures()
                )
                    throw new Error("Wallet changed the reviewed transaction");
                const signature = Base58Util.encode(signed.signature);
                saved.signatures[attempt.attemptUuid] = signature;
                // Persist BEFORE network submission. A lost response can never trigger another signature/payment.
                this.storage.write(saved);
                try {
                    const sent = await this.chain.submit(attempt, bytes);
                    if (sent !== signature) throw new Error("RPC returned a different transaction signature");
                } finally {
                    current = await this.api.report(saved.requestUuid, attempt.attemptUuid, signature);
                    onProgress(current);
                }
            }
            return current;
        });
    }
    private async exclusive<T>(work: () => Promise<T>): Promise<T> {
        if (this.busy) throw new Error("A claim action is already in progress");
        this.busy = true;
        try {
            return await work();
        } finally {
            this.busy = false;
        }
    }
}
